import { useId } from 'react'

export interface RadioOption<T extends string> {
  readonly value: T
  readonly label: string
  readonly disabled?: boolean
}

export interface RadioGroupProps<T extends string> {
  /** Shared `name` for the native radio inputs so they behave as one group
   * (arrow-key selection, single-checked invariant) without any custom JS. */
  readonly name: string
  /** Visually-hidden-capable legend — always rendered for `role="radiogroup"`'s
   * accessible name; pass `legendVisuallyHidden` to hide it visually only. */
  readonly legend: string
  readonly legendVisuallyHidden?: boolean
  readonly options: readonly RadioOption<T>[]
  readonly value: T
  readonly onChange: (value: T) => void
  readonly disabled?: boolean
}

/**
 * Radio / radio-dot primitive (components.md §1 — "Purple-dot variant for
 * RFQ region select"). Native `<input type="radio">` elements sharing one
 * `name`, so keyboard arrow-navigation and the single-checked invariant are
 * free from the browser — no hand-rolled selection state machine to get
 * wrong. The visible legend is a plain `<p>` and the group is an explicit
 * `<div role="radiogroup" aria-label>` (NOT a `<fieldset>`/`<legend>`): this
 * gives `getByRole('radiogroup', { name })` a stable target and avoids
 * nesting a redundant native "group" role inside an explicit one. The
 * purple-dot visual (vs. the browser default) is pure CSS on top of the
 * native input (`.radio-dot`, ui.css).
 */
export function RadioGroup<T extends string>({
  name,
  legend,
  legendVisuallyHidden = false,
  options,
  value,
  onChange,
  disabled = false,
}: RadioGroupProps<T>) {
  const baseId = useId()

  return (
    <div className="radio-group">
      <p className={legendVisuallyHidden ? 'sr-only' : 'form-field-label'}>{legend}</p>
      <div role="radiogroup" aria-label={legend} className="radio-group-options">
        {options.map((option) => {
          const inputId = `${baseId}-${option.value}`
          return (
            <label key={option.value} htmlFor={inputId} className="radio-option">
              <input
                id={inputId}
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                disabled={disabled || option.disabled}
                onChange={() => onChange(option.value)}
                className="radio-dot"
              />
              {option.label}
            </label>
          )
        })}
      </div>
    </div>
  )
}
